import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";

import { ConfirmationService, ConfirmEventType } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { UserService } from "../../../shared/services/user.service";
import { PersonSelectorService } from "../../personselector/personselector.service";

import { DataService_photomaint } from "./shared/data.service";
import { wsImageStagingQueueInfo } from "./shared/wsImageStagingQueueInfo";
import { wsPersonPhotoSelectedInfo, wsPersonPhotoSelectedInfoWithOptions } from "./shared/wsPersonPhotoSelectedInfo";
import { wsPersonPhotoSelectedItemsInfo } from "./shared/wsPersonPhotoSelectedInfo";

import { PhotoInfo } from "./shared/PhotoInfo";
import { CheckedInfo } from "./shared/CheckedInfo";
import { PhotoEditComponent } from "./photoedit/photoedit.component";
import { PhotoTransferDialogComponent } from "./photo-transfer-dialog/photo-transfer-dialog.component";

@Component({
	selector: "app-root",
	templateUrl: "./photomaint.component.html",
	styleUrls: ["./photomaint.component.css"],
	providers: [ConfirmationService, DialogService],
})
export class PhotoMaintComponent implements OnInit, OnDestroy {
	constructor(
		private dataService: DataService_photomaint,
		private userService: UserService,
		private confirmationService: ConfirmationService,
		private personselectorService: PersonSelectorService,
		private dialogService: DialogService
	) {}

	//-----------------------------------------------------------------------------------------------------
	//
	//	Globals
	//
	//-----------------------------------------------------------------------------------------------------
	private title = "Photo Maintenance";
	private user: any;
	public isBusy: boolean = false;
	public isShowInfo: boolean = false;
	public isKeepChecked: boolean = false;

	//	Person Selector
	private subscriptionPersonSelect: Subscription;
	private subscriptionSchoolSelect: Subscription;
	private subscriptionPhotoClicked: Subscription;

	private SelectedPerson: any; // From Person Selector
	private SelectedSchool: any; // From Person Selector

	//	Image List
	public ImageStagingQueue: wsImageStagingQueueInfo[] = [];

	//	Image Processing
	private ImageLoadUpload: PhotoInfo[] = [];

	// Image Checked List
	private ImageCheckedList: CheckedInfo[] = [];

	//-----------------------------------------------------------------------------------------------------
	//
	//	ngOnInit
	//
	//-----------------------------------------------------------------------------------------------------
	ngOnInit() {
		this.user = this.userService.getCurrentUser();

		this.subscriptionPersonSelect = this.personselectorService.onPersonSelectChange().subscribe((person) => {
			this.SelectedPerson = person;
		});

		this.subscriptionSchoolSelect = this.personselectorService.onSchoolSelectedChange().subscribe((school) => {
			this.isBusy = true;
			this.SelectedSchool = school;
			this.dataService.ImageStagingQueueSelectList(this.SelectedSchool.SchoolID).subscribe((data) => this.ImageStagingQueueSelectList_Complete(data));
		});

		this.subscriptionPhotoClicked = this.personselectorService.onPhotoClicked().subscribe((personID) => {
			if (personID > 0) {
				this.onEditPhoto(personID);
			}
		});
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	ngOnDestroy
	//
	//-----------------------------------------------------------------------------------------------------

	ngOnDestroy() {
		this.subscriptionPersonSelect.unsubscribe();
		this.subscriptionSchoolSelect.unsubscribe();
		this.subscriptionPhotoClicked.unsubscribe();
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	ImageStagingQueueSelectList_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	ImageStagingQueueSelectList_Complete(data: any) {
		this.ImageStagingQueue = data;

		//
		// Re-check items if needed.
		//
		if (this.isKeepChecked == true) {
			this.isKeepChecked = false;

			this.ImageCheckedList.forEach((element) => {
				let ImageStagingQueueID = element.ImageStagingQueueID;

				this.ImageStagingQueue.forEach((element) => {
					if (element.ImageStagingQueueID == ImageStagingQueueID) {
						element.IsChecked = true;
					}
				});
			});
		}

		this.isBusy = false;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onSelectItem
	//
	//-----------------------------------------------------------------------------------------------------
	onSelectItem(event: any, data: wsImageStagingQueueInfo) {
		//
		//	De-Select All Items, But the one Just Selected
		//
		this.ImageStagingQueue.forEach((element) => {
			if (element.IsSelected == true && element.ImageStagingQueueID != data.ImageStagingQueueID) {
				element.IsSelected = false;
			}
		});

		data.IsSelected = true;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onCheckItem
	//
	//-----------------------------------------------------------------------------------------------------
	onCheckItem(event: any, data: wsImageStagingQueueInfo) {
		data.IsChecked = !data.IsChecked;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onToggleInfo
	//
	//-----------------------------------------------------------------------------------------------------
	onToggleInfo() {
		this.isShowInfo = !this.isShowInfo;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onAddPhoto
	//
	//-----------------------------------------------------------------------------------------------------
	onAddPhoto(event: any) {
		if (this.SelectedSchool.SchoolID > 1) {
			console.log("Starting image load..", new Date().toLocaleTimeString("en-US"));

			this.ImageLoadUpload = [];

			if (event.target.files && event.target.files[0]) {
				let reader = new FileReader();

				reader.onload = (e: any) => {
					this.ResizeImageAndUpload(event.target.files[0], e.target.result, 640, this);
				};

				reader.readAsDataURL(event.target.files[0]);
			}
		} else {
			this.confirmationService.confirm({
				acceptLabel: "Ok",
				rejectVisible: false,
				message: "Please select a school before adding photo.",
				header: "School not selected",
				icon: "fa fa-external-link",
				accept: () => {},
				reject: () => {},
			});
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onEditPhoto
	//
	//-----------------------------------------------------------------------------------------------------
	onEditPhoto(PersonSelecterPersonID: number = -1) {
		if (PersonSelecterPersonID > 0) {
			//
			//	Edit Photo From CAASS
			//
			//	closable: false = Hide X button and disable Esc
			//
			const ref = this.dialogService.open(PhotoEditComponent, {
				header: "Edit Photo In CAASS",
				width: "700px",
				contentStyle: { overflow: "auto" },
				baseZIndex: 10000,
				data: {
					SelectedSchool: this.SelectedSchool,
					ImageStagingQueueItem: null,
					PersonSelecterPersonID: PersonSelecterPersonID,
				},
			});

			ref.onClose.subscribe((response) => {
				if (response == 2) {
					//
					//	Refresh Person Selector Image
					//
					this.personselectorService.ThumbnailRefresh("Refresh");
				}
			});
		} else {
			//
			//	Edit Photo From Image Staging Queue
			//
			if (this.ImageStagingQueue.length > 0) {
				var lstFilter = this.ImageStagingQueue.filter(function (obj) {
					return obj.IsSelected == true;
				});

				if (lstFilter.length > 0) {
					//
					//	Use the first Item, Should only ever be one item selected at a time.
					//
					var ref = this.dialogService.open(PhotoEditComponent, {
						header: "Edit Photo In Staging Area",
						width: "695px",
						data: {
							SelectedSchool: this.SelectedSchool,
							ImageStagingQueueItem: lstFilter[0],
							PersonSelecterPersonID: -1,
						},
					});
					ref.onClose.subscribe((response) => {
						if (response == 1) {
							//
							// Clear Checked List
							//
							this.ImageCheckedList = [];

							this.ImageStagingQueue.forEach((element) => {
								//
								//	Save Checked Items, Used when list is reloaded after edit/save is complete.
								//
								if (element.IsChecked == true) {
									let r: CheckedInfo = {
										ImageStagingQueueID: element.ImageStagingQueueID,
									};

									this.ImageCheckedList.push(r);
								}
							});
							//
							//	Refresh Photo List
							//
							this.isKeepChecked = true;
							this.dataService
								.ImageStagingQueueSelectList(this.SelectedSchool.SchoolID)
								.subscribe((data) => this.ImageStagingQueueSelectList_Complete(data));
						}
					});
				}
			}
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onCheckAll
	//
	//-----------------------------------------------------------------------------------------------------
	onCheckAll() {
		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == false) {
				element.IsChecked = true;
			}
		});
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onUnCheckAll
	//
	//-----------------------------------------------------------------------------------------------------
	onUnCheckAll() {
		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true) {
				element.IsChecked = false;
			}
		});
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onAutoAssign
	//
	//-----------------------------------------------------------------------------------------------------
	onAutoAssign() {
		var intAssignCount = 0;

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID < 1) {
				intAssignCount += 1;
			}
		});

		if (intAssignCount > 0) {
			//
			// Confirm Upload
			//
			var strWork = "";
			if (intAssignCount == 1) {
				strWork = "photo";
			} else {
				strWork = "photos";
			}

			var message =
				"You are about to attempt to auto assign " +
				intAssignCount +
				" " +
				strWork +
				" " +
				"in your staging area.<br><br>" +
				"Do you wish to continue?";

			this.confirmationService.confirm({
				message: message,
				header: "Auto Assign",
				icon: "fa fa-external-link",
				accept: () => {
					this.onAutoAssignConfirmed();
				},
				reject: () => {},
			});
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onAutoAssignConfirmed
	//
	//-----------------------------------------------------------------------------------------------------
	onAutoAssignConfirmed() {
		let SelectedItems: wsPersonPhotoSelectedInfo = {
			SchoolID: this.SelectedSchool.SchoolID,
			ItemList: [],
		};

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID < 1) {
				let r: wsPersonPhotoSelectedItemsInfo = {
					ImageStagingQueueID: element.ImageStagingQueueID,
					PersonID: element.PersonID,
				};

				SelectedItems.ItemList.push(r);
			}
		});

		if (SelectedItems.ItemList.length > 0) {
			this.isBusy = true;
			this.dataService.ImageStagingQueueAssign(SelectedItems).subscribe((data) => this.onAutoAssign_Complete(data));
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onAutoAssign_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	onAutoAssign_Complete(data: any) {
		this.ImageStagingQueue = data;
		this.isBusy = false;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onUnAssign
	//
	//-----------------------------------------------------------------------------------------------------
	onUnAssign() {
		var intUnAssignCount = 0;

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID > 0) {
				intUnAssignCount += 1;
			}
		});

		if (intUnAssignCount > 0) {
			//
			// Confirm Upload
			//
			var strWork = "";
			if (intUnAssignCount == 1) {
				strWork = "photo";
			} else {
				strWork = "photos";
			}

			var message = "You are about to Unassign " + intUnAssignCount + " " + strWork + " " + "in your staging area.<br><br>" + "Do you wish to continue?";

			this.confirmationService.confirm({
				message: message,
				header: "Unassign",
				icon: "fa fa-undo",
				accept: () => {
					this.onUnAssignConfirmed();
				},
				reject: () => {},
			});
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onUnAssignConfirmed
	//
	//-----------------------------------------------------------------------------------------------------
	onUnAssignConfirmed() {
		let SelectedItems: wsPersonPhotoSelectedInfo = {
			SchoolID: this.SelectedSchool.SchoolID,
			ItemList: [],
		};

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID > 0) {
				let r: wsPersonPhotoSelectedItemsInfo = {
					ImageStagingQueueID: element.ImageStagingQueueID,
					PersonID: element.PersonID,
				};

				SelectedItems.ItemList.push(r);
			}
		});

		if (SelectedItems.ItemList.length > 0) {
			this.isBusy = true;
			this.dataService.ImageStagingQueueUnassign(SelectedItems).subscribe((data) => this.onUnAssign_Complete(data));
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onUnAssign_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	onUnAssign_Complete(data: any) {
		this.ImageStagingQueue = data;
		this.isBusy = false;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onDelete
	//
	//-----------------------------------------------------------------------------------------------------
	onDelete() {
		var intDeleteCount = 0;

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true) {
				intDeleteCount += 1;
			}
		});

		if (intDeleteCount > 0) {
			//
			// Confirm Upload
			//
			var strWork = "";
			if (intDeleteCount == 1) {
				strWork = "photo";
			} else {
				strWork = "photos";
			}

			var message = "You are about to delete " + intDeleteCount + " " + strWork + " " + "from your staging area.<br><br>" + "Do you wish to continue?";

			this.confirmationService.confirm({
				message: message,
				header: "Photo Delete",
				icon: "fa fa-trash",
				accept: () => {
					this.onDeleteConfirmed();
				},
				reject: () => {},
			});
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onDeleteConfirmed
	//
	//-----------------------------------------------------------------------------------------------------
	onDeleteConfirmed() {
		let SelectedItems: wsPersonPhotoSelectedInfo = {
			SchoolID: this.SelectedSchool.SchoolID,
			ItemList: [],
		};

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true) {
				let r: wsPersonPhotoSelectedItemsInfo = {
					ImageStagingQueueID: element.ImageStagingQueueID,
					PersonID: element.PersonID,
				};

				SelectedItems.ItemList.push(r);
			}
		});

		if (SelectedItems.ItemList.length > 0) {
			this.isBusy = true;
			this.dataService.ImageStagingQueueDelete(SelectedItems).subscribe((data) => this.onDelete_Complete(data));
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onDelete_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	onDelete_Complete(data: any) {
		this.ImageStagingQueue = data;
		this.isBusy = false;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onManualAssign
	//
	//-----------------------------------------------------------------------------------------------------
	onManualAssign() {
		if (this.SelectedPerson.PersonID > 0) {
			let SelectedItems: wsPersonPhotoSelectedInfo = {
				SchoolID: this.SelectedSchool.SchoolID,
				ItemList: [],
			};

			//
			// Clear Checked List
			//
			this.ImageCheckedList = [];

			this.ImageStagingQueue.forEach((element) => {
				if (element.IsSelected == true) {
					let r: wsPersonPhotoSelectedItemsInfo = {
						ImageStagingQueueID: element.ImageStagingQueueID,
						PersonID: this.SelectedPerson.PersonID,
					};

					SelectedItems.ItemList.push(r);
				}

				//
				//	Save Checked Items, Used when list is reloaded after manual assign is complete.
				//
				if (element.IsChecked == true) {
					let r: CheckedInfo = {
						ImageStagingQueueID: element.ImageStagingQueueID,
					};

					this.ImageCheckedList.push(r);
				}
			});

			if (SelectedItems.ItemList.length > 0) {
				this.isKeepChecked = true;
				this.isBusy = true;
				this.dataService.ImageStagingQueueManualAssign(SelectedItems).subscribe((data) => this.onManualAssign_Complete(data));
			}
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onManualAssign_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	onManualAssign_Complete(data: any) {
		this.ImageStagingQueue = data;

		//
		// Re-check items if needed.
		//
		if (this.isKeepChecked == true) {
			this.isKeepChecked = false;

			this.ImageCheckedList.forEach((element) => {
				let ImageStagingQueueID = element.ImageStagingQueueID;

				this.ImageStagingQueue.forEach((element) => {
					if (element.ImageStagingQueueID == ImageStagingQueueID) {
						element.IsChecked = true;
					}
				});
			});
		}

		this.isBusy = false;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onTransfer
	//
	//-----------------------------------------------------------------------------------------------------
	onTransfer() {
		var intTransferCount = 0;

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID > 0) {
				intTransferCount += 1;
			}
		});

		if (intTransferCount > 0) {
			//
			// Confirm Upload
			//
			var strWork = "";
			if (intTransferCount == 1) {
				strWork = "photo";
			} else {
				strWork = "photos";
			}

			var ref = this.dialogService.open(PhotoTransferDialogComponent, {
				header: "Photo Transfer",
				width: "fit-content",
				data: {
					//INSERT Custom Data here
					lstQueue: this.ImageStagingQueue,
					intTransferCount: intTransferCount,
					strWork: strWork,
					SchoolID: this.SelectedSchool.SchoolID,
				},
			});
			ref.onClose.subscribe((response: any) => {
				if (response.TransferOption != undefined) {
					this.onTransferConfirmedWithOptions(
						response.TransferOption == "TransferOnly" ? 0 : 1, //Add to Queue
						response.TransferOption == "SkipToPrint" ? 1 : 0, //Print Right away?
						response.Student_IDPrinterID,
						response.Student_IDCardTemplateID,
						response.Staff_IDPrinterID,
						response.Staff_IDCardTemplateID,
						response.Student_SortID,
						response.Student_AcademyID,
						response.Student_GradeLevelID,
						response.Student_HomeroomID
					);
				}
			});
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onTransferConfirmed
	//
	//-----------------------------------------------------------------------------------------------------
	onTransferConfirmed(AddToPrintQ: number) {
		let SelectedItems: wsPersonPhotoSelectedInfo = {
			SchoolID: this.SelectedSchool.SchoolID,
			ItemList: [],
		};

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID > 0) {
				let r: wsPersonPhotoSelectedItemsInfo = {
					ImageStagingQueueID: element.ImageStagingQueueID,
					PersonID: element.PersonID,
				};

				SelectedItems.ItemList.push(r);
			}
		});

		if (SelectedItems.ItemList.length > 0) {
			this.isBusy = true;

			if (AddToPrintQ == 1) {
				this.dataService.ImageStagingQueueTransferAddToQ(SelectedItems).subscribe((data) => this.onTransfer_Complete(data));
			} else {
				this.dataService.ImageStagingQueueTransfer(SelectedItems).subscribe((data) => this.onTransfer_Complete(data));
			}
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onTransfer_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	onTransfer_Complete(data: any) {
		this.ImageStagingQueue = data;
		this.isBusy = false;

		//
		//	Refresh Person Selector Image
		//
		this.personselectorService.ThumbnailRefresh("Refresh");
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onTransferConfirmedWithOptions
	//
	//-----------------------------------------------------------------------------------------------------
	onTransferConfirmedWithOptions(
		AddToPrintQ: number,
		SkipToPrint: number,
		Student_IDPrinterID: number,
		Student_IDCardTemplateID: string,
		Staff_IDPrinterID: number,
		Staff_IDCardTemplateID: string,
		Student_SortID: number,
		Student_AcademyID: number,
		Student_GradeLevelID: number,
		Student_HomeroomID: number
	) {
		let SelectedItems: wsPersonPhotoSelectedInfoWithOptions = {
			SchoolID: this.SelectedSchool.SchoolID,
			ItemList: [],
			AddToPrintQ: AddToPrintQ,
			SkipToPrint: SkipToPrint,
			Student_IDPrinterID: Student_IDPrinterID,
			Student_IDCardTemplateID: Student_IDCardTemplateID,
			Staff_IDPrinterID: Staff_IDPrinterID,
			Staff_IDCardTemplateID: Staff_IDCardTemplateID,
			Student_SortID: Student_SortID,
			Student_AcademyID: Student_AcademyID,
			Student_GradeLevelID: Student_GradeLevelID,
			Student_HomeroomID: Student_HomeroomID,
		};

		this.ImageStagingQueue.forEach((element) => {
			if (element.IsChecked == true && element.PersonID > 0) {
				let r: wsPersonPhotoSelectedItemsInfo = {
					ImageStagingQueueID: element.ImageStagingQueueID,
					PersonID: element.PersonID,
				};
				SelectedItems.ItemList.push(r);
			}
		});

		if (SelectedItems.ItemList.length > 0) {
			this.isBusy = true;
			this.dataService.ImageStagingQueueTransferWithOptions(SelectedItems).subscribe((data) => this.onTransferWithOptions_Complete(data));
		}
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	onTransferWithOptions_Complete
	//
	//-----------------------------------------------------------------------------------------------------
	onTransferWithOptions_Complete(data: any) {
		if (data.Success != undefined && data.Success == false) {
			//Fail Dialog
			this.confirmationService.confirm({
				acceptLabel: "Ok",
				rejectVisible: false,
				message: "Could not transfer the photos: " + data.ErrorMessage,
				header: "ERROR",
				icon: "fa",
				accept: () => {},
			});
		}

		if (data.lstInfo != undefined) {
			this.ImageStagingQueue = data.lstInfo;
		}

		this.isBusy = false;

		//
		//	Refresh Person Selector Image
		//
		this.personselectorService.ThumbnailRefresh("Refresh");
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	ResizeImageAndUpload
	//
	//-----------------------------------------------------------------------------------------------------
	ResizeImageAndUpload(imgfile: any, imgfiledata: any, MAX_SIZE: any, _this: this) {
		// Create work image for resizing
		let img = document.createElement("img");

		img.onerror = function () {
			console.log("ERROR: Invalid Image! -> ", imgfile.name);
		};

		img.onload = function () {
			let width = img.width;
			let height = img.height;

			//
			// Debug Loaded Image {mws}
			//
			//console.log(img);			

			if (width >= height) {
				// Landscape
				height = Math.round(img.height * (MAX_SIZE / img.width));
				width = MAX_SIZE;
			} else {
				// Portrait
				width = Math.round(img.width * (MAX_SIZE / img.height));
				height = MAX_SIZE;
			}

			let canvas = document.createElement("canvas");

			canvas.width = width;
			canvas.height = height;

			let ctx = canvas.getContext("2d");

			ctx.drawImage(img, 0, 0, width, height);

			let obj = {
				PhotoName: imgfile.name,
				PhotoOrientation: 1,
				PhotoDateTime: new Date(imgfile.lastModified),
				PhotoUrl: canvas.toDataURL("image/jpeg"),
				PhotoIsUploaded: false,
			};

			_this.ImageLoadUpload.push(obj);
			console.log("Image load complete..", new Date().toLocaleTimeString("en-US"));

			//
			//	Upload Image
			//
			console.log("Starting image upload..", new Date().toLocaleTimeString("en-US"));
			_this.isBusy = true;
			_this.dataService
				.ImageStagingQueueUpload(_this.SelectedSchool.SchoolID, _this.ImageLoadUpload[0])
				.subscribe((data) => _this.UploadImage_Callback(data));
		};

		// Load The Image Data
		img.src = imgfiledata;
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	UploadImage_Callback
	//
	//-----------------------------------------------------------------------------------------------------
	UploadImage_Callback(data: any) {
		this.ImageLoadUpload[0].PhotoIsUploaded = true;
		console.log("Image upload complete..", new Date().toLocaleTimeString("en-US"));
		//
		//	Refresh Photo List
		//
		this.isBusy = true;
		this.dataService.ImageStagingQueueSelectList(this.SelectedSchool.SchoolID).subscribe((data) => this.ImageStagingQueueSelectList_Complete(data));
	}

	//-----------------------------------------------------------------------------------------------------
	//
	//	Add New Method Here
	//
	//-----------------------------------------------------------------------------------------------------
}
